import { useCallback, useEffect, useRef, useState } from 'react'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { ArtifactExportType } from '@/analytics/enums/artifact-template.enums'
import { kNoMatterId } from '@/analytics/constants'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { fetchArtifactDocxBlobToSave } from '@/artifacts/fetch/get-download-artifact-docx'
import { selectArtifactById } from '@/artifacts/store/selectors'
import { generateDefaultFilename } from '@/artifacts/util/generate-default-filename'
import { uploadBlobToUserFiles } from '@/content-viewer/components/common/save-to-user-files-modal/actions/save-artifact-to-user-files'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { matterPaths } from '@/routing/paths'
import type { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { selectRootFolderIdForMatter } from '@/user_files/folders/store/selectors'

type UseArtifactReadOnlyExportArgs = {
  artifactVersionId: string
  matterId: string | null
  title: string
  conversationId: string
}

type UseArtifactReadOnlyExportResult = {
  isExporting: boolean
  onSave: () => Promise<void>
}

// Read-only collaborators must ignore owner-scoped template state. This hook only uses
// the collaborator-authorized plain DOCX backend path before saving into matter files.
export function useArtifactReadOnlyExport({
  artifactVersionId,
  matterId,
  title,
  conversationId,
}: UseArtifactReadOnlyExportArgs): UseArtifactReadOnlyExportResult {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const newFilesUxEnabled = kGetFeatureFlagStatus(kFeatureFlags.NEW_FILES_UX)
  const rootFolderId = useAppSelector((state: RootState) => (matterId ? selectRootFolderIdForMatter(state, { matterId }) : null))
  const artifactId = useAppSelector((state: RootState) => selectArtifactById(state, { artifactVersionId })?.artifact_id)
  const [isExporting, setIsExporting] = useState(false)
  const isSavingRef = useRef(false)

  useEffect(() => {
    if (matterId && newFilesUxEnabled) dispatch(UserFoldersActions.fetchRootFolder({ matterId }))
  }, [matterId, newFilesUxEnabled, dispatch])

  const handleSave = useCallback(async () => {
    if (isSavingRef.current || !matterId || !rootFolderId) return

    isSavingRef.current = true
    setIsExporting(true)

    try {
      const { blob } = await fetchArtifactDocxBlobToSave(artifactVersionId)
      await uploadBlobToUserFiles(blob, generateDefaultFilename(title), rootFolderId, dispatch)
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId: rootFolderId }))
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: 'Saved to matter files.',
          durationMs: kToastDurationShortMs,
          withLink: { label: 'View files', href: matterPaths.userFiles.folder(matterId, rootFolderId) },
        })
      )
      trackEvent(AnalyticsEvent.ArtifactExportCompleted, {
        artifactVersionId,
        ...(artifactId ? { artifactId } : {}),
        artifactExportType: ArtifactExportType.SaveToMatter,
        templateApplied: false,
        conversationId,
        matterId: matterId ?? kNoMatterId,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save document. Please try again.'
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationShortMs }))
    } finally {
      isSavingRef.current = false
      setIsExporting(false)
    }
  }, [artifactVersionId, matterId, rootFolderId, dispatch, title, trackEvent, artifactId, conversationId])

  return {
    isExporting,
    onSave: handleSave,
  }
}
